import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import i18n from '@/lib/i18n';
import { customerSchema, type Customer } from '@/lib/schemas';

/**
 * עריכת פרטי לקוח (PATCH /customers/:id). מראה את UpdateCustomerDto: שם חובה,
 * השאר אפשר לרוקן — שדה ריק נשלח כמו שהוא, והשרת שומר אותו כ"אין".
 * נשלחים רק שדות שהשתנו, כדי שעריכה של טלפון לא תדרוס כתובת שמישהו אחר עדכן.
 */
const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, i18n.t('customers:form.nameRequired'))
    .max(200, i18n.t('customers:form.nameMax')),
  email: z
    .string()
    .trim()
    .max(320, i18n.t('customers:form.emailMax'))
    .email(i18n.t('customers:form.emailInvalid'))
    .or(z.literal('')),
  phone: z.string().trim().max(40, i18n.t('customers:form.phoneMax')),
  address: z.string().trim().max(500, i18n.t('customers:form.addressMax')),
  taxId: z
    .string()
    .trim()
    .regex(/^(\d{5,10})?$/, i18n.t('customers:edit.taxIdInvalid')),
  // '' = ברירת המחדל של תהליך ההפקה בחשבונית ירוקה.
  billingMode: z.enum(['', 'MONTHLY', 'PER_JOB']),
});
type FormValues = z.infer<typeof schema>;

const toForm = (c: Customer): FormValues => ({
  name: c.name,
  email: c.email ?? '',
  phone: c.phone ?? '',
  address: c.address ?? '',
  taxId: c.taxId ?? '',
  billingMode: c.billingMode ?? '',
});

export function EditCustomerDialog({ customer }: { customer: Customer }) {
  const { t } = useTranslation(['customers', 'common']);
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toForm(customer) });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const before = toForm(customer);
      const changed = Object.fromEntries(
        (Object.keys(values) as Array<keyof FormValues>)
          .filter((k) => values[k].trim() !== before[k])
          .map((k) => [k, k === 'email' ? values[k].trim().toLowerCase() : values[k].trim()]),
      );
      return request(`/customers/${customer.id}`, { method: 'PATCH', body: changed, schema: customerSchema });
    },
    onSuccess: (updated) => {
      qc.setQueryData(['customers', 'detail', customer.id], updated);
      void qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(t('edit.updated'));
      setOpen(false);
    },
    onError: (err: unknown) =>
      toast.error(t('edit.failed'), err instanceof ApiError ? err.message : t('form.unexpectedError')),
  });

  const onSubmit = handleSubmit((values) => save.mutateAsync(values).catch(() => undefined));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // כל פתיחה מתחילה מהערכים העדכניים, לא ממה שנשאר מעריכה שבוטלה.
        if (next) reset(toForm(customer));
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary">
          <Pencil aria-hidden />
          {t('common:actions.edit')}
        </Button>
      </DialogTrigger>
      <DialogContent aria-describedby="edit-customer-desc">
        <DialogHeader>
          <DialogTitle>{t('edit.title')}</DialogTitle>
          <DialogDescription id="edit-customer-desc">
            {t('edit.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            <Field label={t('form.name')} htmlFor="edit-name" error={errors.name?.message} required>
              <Input id="edit-name" autoComplete="off" invalid={!!errors.name} {...register('name')} />
            </Field>
            <Field
              label={t('form.phone')}
              htmlFor="edit-phone"
              error={errors.phone?.message}
              hint={t('edit.phoneHint')}
            >
              <Input id="edit-phone" type="tel" dir="ltr" autoComplete="off" invalid={!!errors.phone} {...register('phone')} />
            </Field>
            <Field label={t('form.email')} htmlFor="edit-email" error={errors.email?.message}>
              <Input id="edit-email" type="email" dir="ltr" autoComplete="off" invalid={!!errors.email} {...register('email')} />
            </Field>
            <Field label={t('form.address')} htmlFor="edit-address" error={errors.address?.message}>
              <Input id="edit-address" autoComplete="off" invalid={!!errors.address} {...register('address')} />
            </Field>
            <Field
              label={t('edit.taxId')}
              htmlFor="edit-tax-id"
              error={errors.taxId?.message}
              hint={t('edit.taxIdHint')}
            >
              <Input
                id="edit-tax-id"
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                invalid={!!errors.taxId}
                {...register('taxId')}
              />
            </Field>
            <Field label={t('edit.billing')} htmlFor="edit-billing-mode" hint={t('edit.billingHint')}>
              <Controller
                control={control}
                name="billingMode"
                render={({ field }) => (
                  <Select
                    value={field.value || 'DEFAULT'}
                    onValueChange={(v) => field.onChange(v === 'DEFAULT' ? '' : v)}
                  >
                    <SelectTrigger id="edit-billing-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DEFAULT">{t('edit.billingDefault')}</SelectItem>
                      <SelectItem value="MONTHLY">{t('edit.billingMonthly')}</SelectItem>
                      <SelectItem value="PER_JOB">{t('edit.billingPerJob')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" loading={save.isPending}>
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
